import { useState } from 'react';
import { Check, Pencil, PenLine, X } from 'lucide-react';
import { Button } from './ui/button';
import { DraftEditor } from './DraftEditor';
import { useAgentName } from '../agent-name';
import type { WorkDecisions, WorkItem } from './work-item';

/**
 * An escalation as a colleague hands it over (#216): two lines on what happened, the customer's words, the ledger, what
 * was already done, one recommendation, a reply draft, and why it needs a person. Send, edit and dismiss go through the
 * approval and task routes; a suggested reply is only text until Carol is asked to draft it for approval.
 */
export function EscalationCard({ item, work, canDecide }: { item: WorkItem; work: WorkDecisions; canDecide: boolean }) {
  const agent = useAgentName();
  const card = item.escalation!; const draft = card.draft; const { busy } = work;
  const [editing, setEditing] = useState(false);
  const [dismissing, setDismissing] = useState(false); const [reason, setReason] = useState('');
  const pendingDraft = draft?.source === 'approval' && draft.status === 'pending';
  const isTask = item.kind === 'task';
  return <section className="ny-escalation" aria-label={`Escalation: ${card.title}`}>
    <header>
      <h3 className="ny-subject">{card.title}</h3>
      {card.summary.map(line => <p key={line} className="ny-esc-summary">{line}</p>)}
    </header>
    {card.evidence.length > 0 && <div><h4>What they said</h4>
      {card.evidence.map(quote => <blockquote key={quote} className="ny-quote">{quote}</blockquote>)}</div>}
    {card.ledger.length > 0 && <div><h4>What the ledger shows</h4><ul>{card.ledger.map(line => <li key={line}>{line}</li>)}</ul></div>}
    {card.taken.length > 0 && <div><h4>Already done</h4><ul>{card.taken.map(line => <li key={line}>{line}</li>)}</ul></div>}
    <div><h4>{agent} suggests</h4><p><strong>{card.recommendation.label}.</strong> {card.recommendation.reason}</p></div>
    {draft && <div className="ny-email">
      <p className="ny-muted">{draft.source === 'approval' ? `${agent}'s draft${draft.to.length ? ` to ${draft.to.join(', ')}` : ''}, waiting for approval`
        : `Suggested reply. ${card.recommendation.verb === 'draft_reply' ? `${agent} writes the real draft, with the account's own facts, when you ask.`
          : 'Nothing is drafted from it here; write it from the Inbox if you agree.'}`}</p>
      {editing && draft.source === 'approval'
        ? <DraftEditor key={`${draft.approvalId}:${draft.revision}`} subject={draft.subject} body={draft.body} busy={busy}
          onSave={(body, subject) => void work.decideEscalationDraft(item, 'edit', { body, subject }).then(() => setEditing(false))}
          onCancel={() => setEditing(false)} />
        : <><p className="ny-subject">{draft.subject}</p>
          <div className="ny-body">{draft.body.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}</div></>}
    </div>}
    <p className="ny-muted">Why a person: {card.needsPerson}</p>
    {canDecide && isTask && !editing && <footer className="ny-actions">
      {dismissing ? <>
        <label className="ny-esc-reason">Why dismiss it?
          <input value={reason} maxLength={500} onChange={event => setReason(event.target.value)} disabled={busy}
            placeholder="For example: settled by phone" /></label>
        <Button variant="ghost" disabled={busy} onClick={() => setDismissing(false)}>Cancel</Button>
        <Button variant="outline" disabled={busy || reason.trim().length < 3}
          onClick={() => void work.dismissTask(item, reason.trim()).then(() => setDismissing(false))}><X aria-hidden />Dismiss</Button>
      </> : <>
        <Button variant="ghost" disabled={busy} onClick={() => setDismissing(true)}><X aria-hidden />Dismiss with a reason</Button>
        {pendingDraft && <Button variant="outline" disabled={busy} onClick={() => setEditing(true)}><Pencil aria-hidden />Edit</Button>}
        {pendingDraft && <Button disabled={busy} onClick={() => void work.decideEscalationDraft(item, 'approve')}><Check aria-hidden />Send as drafted</Button>}
        {card.recommendation.verb === 'draft_reply' && <Button disabled={busy} onClick={() => void work.draftEscalationReply(item)}>
          <PenLine aria-hidden />Draft this reply</Button>}
      </>}
    </footer>}
  </section>;
}
